// Small date helpers shared by planner.component.ts and
// planner-nav-picker.component.ts. Self-contained (plain Date math, no
// date library) so Planner has no dependency on Timer's own copies of the
// same formulas.

export function getISOWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

export function getMondayOf(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay() || 7; // Sunday -> 7
    d.setDate(d.getDate() - (day - 1));
    return d;
}

export function addDays(date: Date, n: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

export function isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

// "3h 30m" duration label, no seconds — every Planner block is a whole
// multiple of the 30-minute snap grid, unlike Timer's live, second-precision
// entries (formatDurationHMS), so seconds would never be meaningful here.
// Drops whichever side is zero ("3h", "30m") rather than always showing
// both.
/** Reads a typed duration, in minutes: "1h 30m", "1h", "30m", "1h30",
 *  "90m", or the older "1:30" and "1.5" (hours) — null if unreadable. The
 *  planner's duration fields show durations as formatDurationHM below. */
export function parseDurationHM(text: string): number | null {
    const t = (text || '').trim().toLowerCase();
    if (!t) return null;
    const hm = /^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m?)?$/.exec(t);
    if (hm && (hm[1] !== undefined || hm[2] !== undefined) && /[hm]/.test(t)) {
        const h = hm[1] !== undefined ? parseFloat(hm[1]) : 0;
        const m = hm[2] !== undefined ? parseInt(hm[2], 10) : 0;
        if (!Number.isFinite(h) || !Number.isFinite(m) || (hm[1] !== undefined && m > 59)) return null;
        return Math.round(h * 60) + m;
    }
    if (t.includes(':')) {
        const [hs, ms] = t.split(':');
        const h = parseInt(hs || '0', 10);
        const m = parseInt(ms || '0', 10);
        if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || m < 0 || m > 59) return null;
        return h * 60 + m;
    }
    if (!/^\d+(\.\d+)?$/.test(t)) return null;
    return Math.round(parseFloat(t) * 60);
}

/** Minutes as hours for the day totals: "3" for whole hours (not "3.0"),
 *  "1.5" otherwise — one decimal place at most. */
export function formatHours(totalMinutes: number): string {
    const hours = Math.round((totalMinutes / 60) * 10) / 10;
    return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}

/** "09:00 – 12:30" — a block's start and end, 24-hour. */
export function formatTimeRange(start: Date, end: Date): string {
    const hm = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return `${hm(start)} – ${hm(end)}`;
}

export function formatDurationHM(totalMinutes: number): string {
    const minutes = Math.max(0, Math.round(totalMinutes));
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
}
